import { webTranslations } from "../webTranslations";

export interface PersistentAudioMedia {
    id: string;
    url: string;
    title: string;
    artist: string;
    artworkUrl?: string;
    detailUrl: string | null;
}

export function showPlayer(root: HTMLElement): void {
    root.hidden = false;
    root.classList.add("persistent-audio-player--visible");
    syncPlayerVisibility(root);
}

export function hidePlayer(root: HTMLElement): void {
    root.hidden = true;
    root.classList.remove("persistent-audio-player--visible");
    syncPlayerVisibility(root);
}

export function syncPlayerVisibility(root: HTMLElement): void {
    document.body.classList.toggle(
        "audio-player-visible",
        !root.hidden && root.classList.contains("persistent-audio-player--visible")
    );
}

export function applyMedia(root: HTMLElement, audio: HTMLAudioElement, media: PersistentAudioMedia): void {
    if (audio.dataset.mediaId !== media.id) {
        advanceMediaRevision(audio);
        audio.src = media.url;
        audio.dataset.mediaId = media.id;
        audio.currentTime = 0;
    }

    setText(root, "[data-persistent-audio-player-title]", media.title);
    setText(root, "[data-persistent-audio-player-artist]", media.artist);
    root.querySelectorAll<HTMLAnchorElement>("[data-persistent-audio-player-detail-link]").forEach(link => {
        if (typeof media.detailUrl === "string" && media.detailUrl.trim()) {
            link.href = media.detailUrl;
            const label = link.dataset.persistentAudioPlayerDetailLabel;
            if (label) {
                link.setAttribute("aria-label", label);
            }
        } else {
            if (document.activeElement === link) {
                root.querySelector<HTMLButtonElement>("[data-persistent-audio-player-toggle-button]")?.focus();
            }
            link.removeAttribute("href");
            link.removeAttribute("aria-label");
            link.removeAttribute("role");
            link.removeAttribute("tabindex");
        }
    });

    const artwork = root.querySelector<HTMLImageElement>("[data-persistent-audio-player-artwork]");
    if (artwork) {
        if (media.artworkUrl) {
            artwork.src = media.artworkUrl;
            artwork.hidden = false;
        } else {
            artwork.removeAttribute("src");
            artwork.hidden = true;
        }
    }

    updatePlaybackState(root, audio);
}

export function getMediaRevision(audio: HTMLAudioElement): number {
    return Number.parseInt(audio.dataset.mediaRevision ?? "0", 10) || 0;
}

export function releaseMedia(root: HTMLElement, audio: HTMLAudioElement): void {
    const document = root.ownerDocument;
    const restoreFocus = root.contains(document.activeElement);
    if (audio.dataset.mediaId) advanceMediaRevision(audio);
    audio.pause();
    audio.dataset.mediaId = "";
    audio.removeAttribute("src");
    audio.load();
    setText(root, "[data-persistent-audio-player-title]", "");
    setText(root, "[data-persistent-audio-player-artist]", "");
    root.querySelectorAll("[data-persistent-audio-player-detail-link]").forEach(link => {
        ["href", "aria-label", "role", "tabindex"].forEach(attribute => link.removeAttribute(attribute));
    });
    const artwork = root.querySelector<HTMLImageElement>("[data-persistent-audio-player-artwork]");
    if (artwork) {
        artwork.removeAttribute("src");
        artwork.hidden = true;
    }
    updatePlaybackState(root, audio);
    setText(root, "[data-persistent-audio-player-remaining]", "--:--");
    const progress = root.querySelector<HTMLInputElement>("[data-persistent-audio-player-progress]");
    if (progress) {
        progress.value = "0";
        progress.style.setProperty("--persistent-audio-player-progress", "0%");
    }
    hidePlayer(root);
    if (restoreFocus) {
        const targets = document.querySelectorAll<HTMLElement>(
            '#smartguide-tour-tray [data-tour-tray-poi-card][aria-current], ' +
            '[data-smartguide-bottom-navigation-current="true"], [data-smartguide-bottom-navigation-item]'
        );
        const visible = (element: HTMLElement): boolean =>
            !element.closest('[hidden], [aria-hidden="true"]') && element.getClientRects().length > 0;
        const candidates = Array.from(targets).filter(visible);
        const selectedCard = candidates.find(element => element.hasAttribute("data-tour-tray-poi-card"));
        const currentNavigation = candidates.find(element => element.dataset.smartguideBottomNavigationCurrent === "true");
        (selectedCard ?? currentNavigation ?? candidates[0])?.focus();
    }
}

function advanceMediaRevision(audio: HTMLAudioElement): void {
    audio.dataset.mediaRevision = (getMediaRevision(audio) + 1).toString();
}

export function updatePlaybackState(root: HTMLElement, audio: HTMLAudioElement): void {
    const isPlaying = !audio.paused;
    root.classList.toggle("persistent-audio-player--playing", isPlaying);

    const toggleButton = root.querySelector<HTMLButtonElement>("[data-persistent-audio-player-toggle-button]");
    toggleButton?.setAttribute("aria-label", isPlaying ? webTranslations.pauseAudio : webTranslations.playAudio);

    setText(root, "[data-persistent-audio-player-toggle-label]", isPlaying ? webTranslations.pause : webTranslations.playAudio);
    setText(root, "[data-persistent-audio-player-remaining]", formatRemainingTime(audio.duration, audio.currentTime));

    const progress = root.querySelector<HTMLInputElement>("[data-persistent-audio-player-progress]");
    if (!progress) {
        return;
    }

    progress.min = "0";
    progress.max = "1";
    progress.step = "any";
    const value = Number.isFinite(audio.duration) && audio.duration > 0
        ? Math.min(1, Math.max(0, audio.currentTime / audio.duration))
        : 0;
    progress.value = value.toString();
    progress.style.setProperty("--persistent-audio-player-progress", `${value * 100}%`);
}

export function seekAudioBack(audio: HTMLAudioElement, seconds = 10): void {
    audio.currentTime = Math.max(0, audio.currentTime - seconds);
}

export function seekAudioToProgress(audio: HTMLAudioElement, progressRatio: number): void {
    if (!Number.isFinite(audio.duration) || audio.duration <= 0 || !Number.isFinite(progressRatio)) {
        return;
    }

    const normalizedProgress = Math.min(1, Math.max(0, progressRatio));
    audio.currentTime = normalizedProgress * audio.duration;
}

export function formatRemainingTime(duration: number, currentTime: number): string {
    if (!Number.isFinite(duration) || duration <= 0) {
        return "--:--";
    }

    const remainingSeconds = Math.max(0, Math.round(duration - currentTime));
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;

    return `-${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

function setText(root: HTMLElement, selector: string, text: string): void {
    const element = root.querySelector<HTMLElement>(selector);
    if (element) {
        element.textContent = text;
    }
}
